// ── Project Alignment Section Editor ───────────────────────────────────────────
// Left: client requirements (gold bullets), Right: our capabilities (checkmarks)
// Summary paragraph at bottom

import { Plus, Trash2, CheckCircle } from 'lucide-react';
import { uid } from '../../lib/uid';


export default function ProjectAlignmentSection({ data, onChange }) {
  const alignment = data || { intro: '', points: [], summary: '' };

  const update = (key, value) => {
    onChange({ ...alignment, [key]: value });
  };

  const addPoint = () => {
    const points = [...(alignment.points || []), { id: uid(), requirement: '', our_capability: '' }];
    update('points', points);
  };

  const updatePoint = (idx, field, value) => {
    const points = [...alignment.points];
    points[idx] = { ...points[idx], [field]: value };
    update('points', points);
  };

  const removePoint = (idx) => {
    const points = [...alignment.points];
    points.splice(idx, 1);
    update('points', points);
  };

  return (
    <div className="space-y-4">
      {/* Introduction */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          Introduction
        </label>
        <textarea
          value={alignment.intro || ''}
          onChange={(e) => update('intro', e.target.value)}
          placeholder="Our experience aligns closely with the requirements of this project:"
          rows={2}
          className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
        />
      </div>

      {/* Alignment Points */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-2">
          Alignment Points
        </label>
        {(alignment.points || []).map((point, idx) => (
          <div key={point.id} className="mb-3 p-3 bg-[#f8f7f4] rounded-lg border border-[#e5e4df]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#6b7280]">#{idx + 1}</span>
              <button
                type="button"
                onClick={() => removePoint(idx)}
                className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Left: Requirement */}
              <div>
                <label className="block text-xs font-semibold text-[#c9a84c] uppercase tracking-wider mb-1">
                  Requirement
                </label>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] mt-2 flex-shrink-0" />
                  <textarea
                    value={point.requirement || ''}
                    onChange={(e) => updatePoint(idx, 'requirement', e.target.value)}
                    placeholder="Client requirement..."
                    rows={2}
                    className="flex-1 px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                  />
                </div>
              </div>
              {/* Right: Our Capability */}
              <div>
                <label className="block text-xs font-semibold text-[#1a1a2e] uppercase tracking-wider mb-1">
                  Our Capability
                </label>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1.5 flex-shrink-0" />
                  <textarea
                    value={point.our_capability || ''}
                    onChange={(e) => updatePoint(idx, 'our_capability', e.target.value)}
                    placeholder="How we meet this..."
                    rows={2}
                    className="flex-1 px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addPoint}
          className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium mt-1"
        >
          <Plus className="w-3.5 h-3.5" />
          Add alignment point
        </button>
      </div>

      {/* Summary */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          Summary
        </label>
        <textarea
          value={alignment.summary || ''}
          onChange={(e) => update('summary', e.target.value)}
          placeholder="This alignment ensures we can deliver exceptional results..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
        />
      </div>
    </div>
  );
}
